/**
 * 原生 → Web 业务载荷（`window.__ED_NATIVE_PAYLOAD__` + 桥消息 `native-payload`）。
 *
 * 与埋点 `__ED_TRACKING__` 同型：documentStart 先写全局，池复用靠 page-ready 补推 merge。
 * **仅 App WebView 消费**（`getBridge().isInApp()`）；H5 不订阅、不重试、不挡网络。
 * 契约：`docs/engineering/native-web-bridge-contract.md` §3.2。
 */

import { useEffect, useRef, useState } from "react";
import { getBridge } from "@/src/lib/bridge/bridge";
import { MessageType } from "@/src/lib/bridge/types";
import type { TrackerRecord } from "@features/api/TrackerApi";

declare global {
  interface Window {
    __ED_NATIVE_PAYLOAD__?: Record<string, unknown>;
  }
}

/** Brief 卡堆首屏批次 namespace（KMP `WebNativePayload.NS_BRIEF_FEED`）。 */
export const NATIVE_PAYLOAD_NS_BRIEF_FEED = "brief.feed";

export type NativeBriefFeedPayload = {
  v?: number;
  ownerId?: string;
  startId?: string;
  updatedAt?: number;
  rows?: TrackerRecord[];
};

/** App WebView 才走注入通道；H5 / SSR 一律短路。 */
export function isNativePayloadRuntime(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return getBridge().isInApp();
  } catch {
    return false;
  }
}

function readBag(): Record<string, unknown> {
  if (typeof window === "undefined") {
    return {};
  }
  const bag = window.__ED_NATIVE_PAYLOAD__;
  return bag && typeof bag === "object" ? bag : {};
}

function writeBag(bag: Record<string, unknown>): void {
  if (typeof window === "undefined") {
    return;
  }
  window.__ED_NATIVE_PAYLOAD__ = bag;
}

/** 只读某个 namespace；不存在返回 null。 */
export function readNativePayload<T = unknown>(namespace: string): T | null {
  const value = readBag()[namespace];
  return value == null ? null : (value as T);
}

/**
 * 读出并删除某个 namespace（一次性消费，避免 SPA 内二次导航误用旧批次）。
 */
export function takeNativePayload<T = unknown>(namespace: string): T | null {
  const bag = { ...readBag() };
  const value = bag[namespace];
  if (value == null) {
    return null;
  }
  delete bag[namespace];
  writeBag(bag);
  return value as T;
}

/** 合并桥推送 / 晚到的 bag（浅合并 namespace 键）。 */
export function mergeNativePayloadBag(
  incoming: Record<string, unknown> | null | undefined,
): void {
  if (!incoming || typeof incoming !== "object") {
    return;
  }
  writeBag({ ...readBag(), ...incoming });
}

/**
 * 订阅 `native-payload` 补推。**仅 App 内**；H5 no-op。
 * 宜在业务读 payload 之前装上（与 tracking-context 同序）。
 */
export function subscribeNativePayload(): () => void {
  if (!isNativePayloadRuntime()) {
    return () => undefined;
  }
  try {
    return getBridge().on(MessageType.NATIVE_PAYLOAD, (data: unknown) => {
      if (!data || typeof data !== "object") {
        return;
      }
      mergeNativePayloadBag(data as Record<string, unknown>);
    });
  } catch {
    return () => undefined;
  }
}

function matchBriefFeed(
  feed: NativeBriefFeedPayload | null,
  opts: { ownerId: string; startId: string },
): TrackerRecord[] | null {
  if (!feed || !Array.isArray(feed.rows) || feed.rows.length === 0) {
    return null;
  }
  const ownerId = opts.ownerId?.trim() ?? "";
  if (feed.ownerId && ownerId && feed.ownerId !== ownerId) {
    return null;
  }
  const startId = opts.startId?.trim() ?? "";
  if (startId) {
    const startOk =
      feed.startId === startId || feed.rows.some((row) => row?.id === startId);
    if (!startOk) {
      return null;
    }
  }
  return feed.rows;
}

/**
 * Brief 详情：若注入批次与当前 owner / start 匹配则 take 并返回 rows，否则 null。
 * **不负责环境门禁** —— 由 [useNativeBriefFeed] / 调用方保证只在 App 内调用。
 */
export function takeMatchedBriefFeed(opts: {
  ownerId: string;
  startId: string;
}): TrackerRecord[] | null {
  const feed = readNativePayload<NativeBriefFeedPayload>(
    NATIVE_PAYLOAD_NS_BRIEF_FEED,
  );
  const rows = matchBriefFeed(feed, opts);
  if (!rows) {
    return null;
  }
  takeNativePayload(NATIVE_PAYLOAD_NS_BRIEF_FEED);
  return rows;
}

/**
 * 优先消费原生注入的 Brief 批次。
 *
 * - **非 App WebView**：立刻返回 `null`，不挂桥、不重试（H5 走正常 GET）。
 * - **App 内**：读 documentStart bag；晚到的 `native-payload` / 微任务窗口内再试。
 */
export function useNativeBriefFeed(
  ownerId: string,
  startId: string,
): TrackerRecord[] | null {
  const [rows, setRows] = useState<TrackerRecord[] | null>(null);
  const takenRef = useRef(false);
  const inApp = isNativePayloadRuntime();

  useEffect(() => {
    if (!inApp || !startId || takenRef.current) {
      return;
    }

    const tryApply = () => {
      if (takenRef.current) {
        return;
      }
      const matched = takeMatchedBriefFeed({ ownerId, startId });
      if (matched) {
        takenRef.current = true;
        setRows(matched);
      }
    };

    tryApply();
    let offPush: () => void = () => undefined;
    try {
      offPush = getBridge().on(MessageType.NATIVE_PAYLOAD, () => {
        tryApply();
      });
    } catch {
      // bridge 未就绪
    }
    const t0 = window.setTimeout(tryApply, 0);
    const t1 = window.setTimeout(tryApply, 80);

    return () => {
      offPush();
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [inApp, ownerId, startId]);

  if (!inApp) {
    return null;
  }
  return rows;
}
